/**
 * Lot CYC (V650) — bandeau « suppression programmée » : visible sur TOUTES les routes, pour tous les
 * membres, pendant le délai avant purge. Organisation en cours de suppression (suspendue, lecture
 * seule) ; projet ACTIF en cours de suppression (archivé, lecture seule). L'administration y trouve le
 * lien vers la demande (annuler, télécharger l'export). Silencieux en cas d'erreur : il informe, il ne
 * bloque rien — la notification des propriétaires reste la voie garantie.
 */
import type { JSX } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'

import { CLE_ETAT_CYCLE, cycleDeVieApi } from '@/api/cycleDeVieApi'
import { useT } from '@/i18n/language'
import { formaterInstant } from '@/lib/arretIA'
import { LIEN_CIBLE } from '@/lib/cibles'

export function BandeauCycleDeVie({ organisation, projetActif, administration }: {
  organisation: string | undefined
  projetActif: string | null | undefined
  administration: boolean
}): JSX.Element | null {
  const { t } = useT()
  const etat = useQuery({
    queryKey: [...CLE_ETAT_CYCLE, organisation],
    queryFn: cycleDeVieApi.etat,
    enabled: Boolean(organisation),
    refetchInterval: 60_000,
    retry: false,
  })
  const orga = etat.data?.organisation ?? null
  const projet = (etat.data?.projects ?? []).find((p) => p.workspace_id === projetActif) ?? null
  if (!orga && !projet) return null
  return (
    <section aria-label={t('cycleBandeau.region', 'Suppression programmée')} data-testid="bandeau-cycle-de-vie"
             className="border-b border-[var(--color-error)] bg-[color-mix(in_srgb,var(--color-error)_10%,transparent)] px-3 py-2 text-sm">
      {orga ? (
        <p className="flex flex-wrap items-baseline gap-x-1.5" aria-live="polite" data-testid="bandeau-cycle-organisation">
          <Trash2 className="size-4 shrink-0 self-center" aria-hidden="true" />
          <strong className="font-semibold">
            {t('cycleBandeau.organisation', 'Suppression de l’organisation programmée : purge définitive après le {{quand}}.', { quand: formaterInstant(orga.purge_after) ?? orga.purge_after })}
          </strong>
          <span>{t('cycleBandeau.organisationDetail', 'L’organisation est suspendue, en lecture seule. Seule la plateforme peut annuler.')}</span>
          {administration ? (
            <Link to={`/org/suppressions/${orga.id}`} className={`font-medium ${LIEN_CIBLE}`}>
              {t('cycleBandeau.voirExport', 'Voir la demande et l’export')}
            </Link>
          ) : null}
        </p>
      ) : null}
      {projet ? (
        <p className="flex flex-wrap items-baseline gap-x-1.5" aria-live="polite" data-testid="bandeau-cycle-projet">
          <Trash2 className="size-4 shrink-0 self-center" aria-hidden="true" />
          <strong className="font-semibold">
            {t('cycleBandeau.projet', 'Suppression du projet « {{nom}} » programmée : purge définitive après le {{quand}}.', { nom: projet.name, quand: formaterInstant(projet.purge_after) ?? projet.purge_after })}
          </strong>
          <span>{t('cycleBandeau.projetDetail', 'Le projet est en lecture seule.')}</span>
          {administration ? (
            <Link to={`/org/suppressions/${projet.id}`} className={`font-medium ${LIEN_CIBLE}`}>
              {t('cycleBandeau.voirDemande', 'Voir la demande (annuler, export)')}
            </Link>
          ) : null}
        </p>
      ) : null}
    </section>
  )
}
